import { Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./common/NotificationContext";
import { PrivateRoute } from "./common/PrivateRoute";

import { Layout } from "./components/Layout";
import { MidtransScript } from "@cakrawalajakarta/shared-components";

import Beranda from "./Beranda";
import { KegiatanDetail } from "./pages/KegiatanDetail";
import { Schedule } from "./pages/Schedule";
import { Kontak } from "./pages/Kontak";
import { SectionKategori } from "./sections/SectionKategori";
import { Profile } from "./pages/Profile";
import { MyRegistration } from "./pages/MyRegistration";
import { RegistrationDetail } from "./pages/RegistrationDetail";
import { Results } from "./pages/Results";
import { ResultsDetail } from "./pages/ResultsDetail";
import { Login } from "./auth/Login";
import { FormPendaftaran } from "./form/FormPendaftaran";
import { EntryListPage } from "./pages/EntrylistPage";
import { SuratPernyataan } from "./pages/SuratPernyataan";
import { RaceRegulations } from "./pages/RaceRegulations";
import { RaceResultsUpload } from "./pages/RaceResultsUpload";
import { FormPendaftaranVolunteer } from "./form/FormPendaftaranVolunteer";
import { AdminVolunteer } from "./pages/AdminVolunteer";
import { Participants } from "./pages/Participants";
import { Archived } from "./pages/Archived";
import { ParticipantsEdit } from "./pages/ParticipantsEdit";
import { RPCS } from "./pages/RPCS";
import { RSVPRPC } from "./pages/RSVPRPC";
import { Shuttle } from "./pages/Shuttle";
import { QRISMedicPayment } from "./pages/QRISMedicPayment";
import { Gallery } from "./pages/Gallery";
import Volunteer from "./pages/Volunteer";

// Admin Components
import { AdminLayout } from "./layout/AdminLayout";
import { Dashboard } from "./pages/admin/Dashboard";
import { Vouchers } from "./pages/admin/Vouchers";
import { Racepack } from "./pages/admin/Racepack";
import { Emails } from "./pages/admin/Emails";
import { EventManagement } from "./pages/admin/EventManagement";
import { Participants as AdminParticipants } from "./pages/admin/Participants";
import { VoucherDetail } from "./pages/admin/VoucherDetail";
import { VoucherCreate } from "./pages/admin/VoucherCreate";
import { Finance } from "./pages/admin/Finance";
import { FinancePaypal } from "./pages/admin/FinancePaypal";
import { Users } from "./pages/admin/Users";

// Dynamic imports untuk komponen besar
const Berita = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.Berita,
  }))
);
const TentangKami = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.TentangKami,
  }))
);
const Register = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.Register,
  }))
);
const Actions = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.Actions,
  }))
);
const Verify = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.Verify,
  }))
);
const LupaPassword = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.LupaPassword,
  }))
);
const Sukses = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.Sukses,
  }))
);
const DetailBerita = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.DetailBerita,
  }))
);
const AddNews = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.AddNews,
  }))
);
const SyaratKetentuan = lazy(() =>
  import("./pages/SyaratKetentuan").then((module) => ({
    default: module.SyaratKetentuan,
  }))
);
const KebijakanPrivasi = lazy(() =>
  import("./pages/KebijakanPrivasi").then((module) => ({
    default: module.KebijakanPrivasi,
  }))
);
const EntryList = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.EntryList,
  }))
);
const Xtrifeinnie = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.Xtrifeinnie,
  }))
);
const StravaCallback = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.StravaCallback,
  }))
);
const StravaStats = lazy(() =>
  import("@cakrawalajakarta/shared-components").then((module) => ({
    default: module.StravaStats,
  }))
);

const App = () => {
  return (
    <AuthProvider>
      <NotificationProvider>
        <MidtransScript />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<Beranda />} />
              <Route
                path="kegiatan/:slug/:distance"
                element={<KegiatanDetail />}
              />
              <Route
                path="ultra24/:distance"
                element={<KegiatanDetail forcedSlug="ULTRA24" />}
              />
              <Route path="entrylist/:eventId" element={<EntryList />} />
              <Route path="entrylistpage" element={<EntryListPage />} />
              <Route
                path="Xtrifeinnie"
                element={
                  <PrivateRoute>
                    <Xtrifeinnie />
                  </PrivateRoute>
                }
              />
              <Route path="/strava-callback" element={<StravaCallback />} />
              <Route
                path="/strava-stats"
                element={
                  <PrivateRoute>
                    <StravaStats />
                  </PrivateRoute>
                }
              />
              <Route path="RaceRegulations" element={<RaceRegulations />} />
              <Route path="RaceResultsUpload" element={<RaceResultsUpload />} />
              <Route path="Results" element={<Results />} />
              <Route
                path="results/:category/:name"
                element={<ResultsDetail />}
              />
              <Route path="QRISMedicPayment" element={<QRISMedicPayment />} />
              <Route path="/RPCS" element={<RPCS />} />
              <Route path="/RSVPRPC" element={<RSVPRPC />} />
              <Route path="/Shuttle" element={<Shuttle />} />
              <Route path="Participants" element={<Participants />} />
              <Route path="Archived" element={<Archived />} />
              <Route path="/participants/edit" element={<ParticipantsEdit />} />
              <Route path="Berita" element={<Berita />} />
              <Route path="AdminVolunteer" element={<AdminVolunteer />} />
              <Route path="Schedule" element={<Schedule />} />
              <Route path="Tentangkami" element={<TentangKami />} />
              <Route path="Kontak" element={<Kontak />} />
              <Route path="Login" element={<Login />} />
              <Route path="Register" element={<Register />} />
              <Route path="MyRegistration" element={<MyRegistration />} />
              <Route
                path="/RegistrationDetail"
                element={<RegistrationDetail />}
              />
              <Route path="AddNews" element={<AddNews />} />
              <Route path="news/:id" element={<DetailBerita />} />
              <Route path="SyaratKetentuan" element={<SyaratKetentuan />} />
              <Route path="KebijakanPrivasi" element={<KebijakanPrivasi />} />
              <Route path="SuratPernyataan" element={<SuratPernyataan />} />
              <Route path="Sukses" element={<Sukses />} />
              <Route path="Actions" element={<Actions />} />
              <Route path="Verify" element={<Verify />} />
              <Route path="LupaPassword" element={<LupaPassword />} />
              <Route path="form/:slug" element={<FormPendaftaran />} />
              <Route path="volunteer" element={<Volunteer />} />
              <Route path="Gallery" element={<Gallery />} />
              <Route
                path="form-volunteer"
                element={
                  <PrivateRoute>
                    <FormPendaftaranVolunteer />
                  </PrivateRoute>
                }
              />
              <Route
                path="Profile"
                element={
                  <PrivateRoute>
                    <Profile />
                  </PrivateRoute>
                }
              />
            </Route>

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminLayout />
                </PrivateRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="event" element={<EventManagement />} />
              <Route path="participants" element={<AdminParticipants />} />
              <Route path="users" element={<Users />} />
              <Route path="vouchers" element={<Vouchers />} />
              <Route path="vouchers/create" element={<VoucherCreate />} />
              <Route path="vouchers/:id" element={<VoucherDetail />} />
              <Route path="racepack" element={<Racepack />} />
              <Route path="emails" element={<Emails />} />
              <Route path="finance" element={<Finance />} />
              <Route path="finance-paypal" element={<FinancePaypal />} />
            </Route>
          </Routes>
        </Suspense>
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;
